<template>
  <Dialog :visible="visible" modal :header="t('help.contact.title')" :style="{ width: '25rem' }"
    :dismissableMask="true" @update:visible="(v: boolean) => emit('update:visible', v)">
    <div class="flex flex-col gap-4">
      <p class="text-surface-500 dark:text-surface-400 m-0">
        {{ t('help.contact.subtitle') }}
      </p>

      <div v-if="showPhoto || contact?.name" class="flex items-center gap-3">
        <Avatar v-if="showPhoto" :image="contact?.photo_url" shape="circle" size="xlarge" />
        <div v-if="contact?.name" class="flex flex-col">
          <span class="font-medium">{{ contact.name }}</span>
          <span v-if="contact.role" class="text-sm text-surface-500 dark:text-surface-400">
            {{ contact.role }}
          </span>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <a v-if="contact?.phone" :href="`tel:${contact.phone}`"
          class="flex items-center gap-2 text-primary no-underline hover:underline">
          <i class="pi pi-phone" />
          <span>{{ contact.phone }}</span>
        </a>

        <a v-if="contact?.email" :href="`mailto:${contact.email}`"
          class="flex items-center gap-2 text-primary no-underline hover:underline">
          <i class="pi pi-envelope" />
          <span>{{ contact.email }}</span>
        </a>

        <a v-if="contact?.support_url" :href="contact.support_url" target="_blank" rel="noopener noreferrer"
          class="flex items-center gap-2 text-primary no-underline hover:underline">
          <i class="pi pi-external-link" />
          <span>{{ t('help.contact.support_link') }}</span>
        </a>

      </div>

      <p v-if="!hasAnyChannel" class="text-surface-500 dark:text-surface-400 m-0">
        {{ t('help.contact.empty') }}
      </p>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Dialog from 'primevue/dialog'
import Avatar from 'primevue/avatar'
import { useAppStore } from '@/stores/app'
import type { HelpContactConfig } from '@/models/PublicConfig'

const props = defineProps<{ visible: boolean }>()
const emit = defineEmits<{ 'update:visible': [value: boolean] }>()

const { t } = useI18n()
const appStore = useAppStore()

const contact = computed<HelpContactConfig | undefined>(() => appStore.publicConfig?.help_contact)
const showPhoto = computed(() => Boolean(contact.value?.show_photo && contact.value?.photo_url))
const hasAnyChannel = computed(() =>
  Boolean(contact.value?.phone || contact.value?.email || contact.value?.support_url)
)

// Ensure the config is loaded the first time the modal is opened (idempotent).
watch(
  () => props.visible,
  (open) => {
    if (open) appStore.loadPublicConfig()
  }
)
</script>
